import { ApiClient, ApiError, type DeploymentRecord } from '../lib/api.js';
import {
  el,
  formatDuration,
  formatRelativeTime,
  statusPill,
  type PillKind,
} from '../lib/dom.js';

export interface DeployViewDeps {
  api: ApiClient;
  initialBranch?: string;
  onDeploymentFinished?: () => void;
}

export function renderDeploy(deps: DeployViewDeps): HTMLElement {
  const branchInput = el('input', {
    attrs: {
      type: 'text',
      placeholder: 'main',
      value: deps.initialBranch ?? '',
      autocapitalize: 'off',
      autocorrect: 'off',
      spellcheck: false,
    },
  });
  const startBtn = el('button', { class: 'primary', text: 'Run deploy' });
  let statusPillEl: HTMLElement = statusPill('idle', 'idle', { large: true });
  const statusText = el('span', { text: 'No active deployment' });
  const statusWrap = el('div', {
    class: 'deploy-status',
    children: [statusPillEl, statusText],
  });
  const logBox = el('div', { class: 'log-output', text: '' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  let stream: (() => void) | null = null;
  let watchedId: number | null = null;

  const setStatus = (kind: PillKind, text: string) => {
    const next = statusPill(kind, kind, { large: true });
    statusPillEl.replaceWith(next);
    statusPillEl = next;
    statusText.textContent = text;
  };

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
  };
  const clearAlert = () => alertBox.setAttribute('hidden', '');

  const watch = (deployment: DeploymentRecord) => {
    if (stream) stream();
    watchedId = deployment.id;
    logBox.textContent = '';
    setStatus(deployment.status, `#${deployment.id} · ${deployment.branch}`);
    startBtn.setAttribute('disabled', '');

    stream = deps.api.streamDeploy(deployment.id, {
      onSnapshot: (snap) => {
        setStatus(
          snap.status,
          `#${snap.id} · ${snap.branch} · started ${formatRelativeTime(snap.startedAt)}`,
        );
      },
      onLog: (chunk) => {
        const wasNearBottom =
          logBox.scrollHeight - logBox.scrollTop - logBox.clientHeight < 50;
        logBox.append(document.createTextNode(chunk));
        if (wasNearBottom) logBox.scrollTop = logBox.scrollHeight;
      },
      onFinish: async (status) => {
        const finalKind: PillKind =
          status === 'success' ? 'success' : status === 'timeout' ? 'timeout' : 'failed';
        try {
          const data = await deps.api.getLog(deployment.id);
          setStatus(
            finalKind,
            `#${data.deployment.id} · ${data.deployment.branch} · ${formatDuration(data.deployment.startedAt, data.deployment.finishedAt)}`,
          );
        } catch {
          setStatus(finalKind, `#${deployment.id}`);
        }
        startBtn.removeAttribute('disabled');
        stream = null;
        watchedId = null;
        deps.onDeploymentFinished?.();
      },
      onError: () => {
        showAlert('Lost connection to deploy stream — refresh to reconnect');
      },
    });
  };

  startBtn.addEventListener('click', async () => {
    clearAlert();
    startBtn.setAttribute('disabled', '');
    try {
      const branch = branchInput.value.trim() || undefined;
      const { deployment } = await deps.api.startDeploy(branch);
      watch(deployment);
    } catch (err) {
      startBtn.removeAttribute('disabled');
      const code = err instanceof ApiError ? err.code : undefined;
      const message = err instanceof ApiError ? err.message : 'Failed to start deploy';
      if (code === 'deploy_busy') {
        showAlert('Another deployment is already running. Watch it below.');
        try {
          const current = await deps.api.currentDeploy();
          if (current.active) watch(current.deployment);
        } catch {
          /* ignore */
        }
      } else {
        showAlert(message);
      }
    }
  });

  // On mount, attach to any in-flight deploy.
  void (async () => {
    try {
      const current = await deps.api.currentDeploy();
      if (current.active) watch(current.deployment);
    } catch {
      /* ignore */
    }
  })();

  const view = el('div', {
    children: [
      el('div', {
        class: 'card',
        children: [
          el('h2', { text: 'Deploy' }),
          alertBox,
          el('div', {
            class: 'toolbar',
            children: [
              el('div', {
                attrs: { style: 'flex:1; min-width: 200px;' },
                children: [
                  el('label', {
                    attrs: { style: 'display:block; font-size:.8rem; color:var(--text-muted); margin-bottom:.25rem;' },
                    text: 'Branch (optional — defaults to main on the VPS)',
                  }),
                  branchInput,
                ],
              }),
              startBtn,
            ],
          }),
          statusWrap,
          logBox,
        ],
      }),
    ],
  });

  // Cleanup if unmounted (the parent simply replaces #app, so we listen to that)
  const observer = new MutationObserver(() => {
    if (!view.isConnected && stream) {
      stream();
      stream = null;
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Return helper so the router can re-watch when "Deploy" is selected
  // from the branches view with a specific branch.
  (view as HTMLElement & { setBranch?: (b: string) => void }).setBranch = (b) => {
    branchInput.value = b;
    branchInput.focus();
  };

  void watchedId; // satisfy no-unused
  return view;
}
